<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCashRegister, faShredder, faCheckCircle, faHourglassHalf, faHourglassStart, faClipboardList, faFileInvoiceDollar, faCheck, faExclamationTriangle, faLayerGroup } from '@fal'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import CountUp from 'vue-countup-v3'

library.add(faCashRegister, faShredder, faCheckCircle, faHourglassHalf, faHourglassStart, faClipboardList, faFileInvoiceDollar, faCheck, faExclamationTriangle, faLayerGroup)

defineProps<{
    pageHead?: PageHeadingTS
    title: string
    kasirStats: {
        label: string
        value: number
        icon: string
        color: string
        route: { name: string }
    }[]
    manufactureStats: {
        label: string
        value: number
        icon: string
        color: string
        route: { name: string }
    }[]
    layoutStats: {
        label: string
        value: number
        icon: string
        color: string
        route: { name: string }
    }[]
}>()
</script>

<template>
    <Head :title />
    <PageHeading :data="pageHead" />

    <div class="px-8 py-6 space-y-8">

        <!-- Section: Kasir -->
        <div>
            <div class="flex items-center gap-x-2 mb-4 pb-2 border-b border-gray-200">
                <FontAwesomeIcon icon="fal fa-cash-register" class="text-sky-500" fixed-width aria-hidden="true" />
                <h2 class="text-base font-semibold text-gray-700">Kasir</h2>
            </div>
            <dl class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <Link
                    v-for="(stat, idx) in kasirStats"
                    :key="idx"
                    :href="route(stat.route.name)"
                    class="relative overflow-hidden rounded-lg bg-white border border-gray-200 px-4 py-5 hover:bg-gray-50 transition-colors"
                >
                    <dt class="truncate text-sm font-medium text-gray-400">{{ stat.label }}</dt>
                    <dd class="mt-2 flex items-baseline gap-x-2">
                        <span class="text-3xl font-semibold tracking-tight" :style="{ color: stat.color }">
                            <CountUp :endVal="stat.value" :duration="1.2" :scrollSpyOnce="true" />
                        </span>
                    </dd>
                    <FontAwesomeIcon
                        :icon="stat.icon"
                        class="absolute top-4 right-4 text-2xl opacity-20"
                        :style="{ color: stat.color }"
                        fixed-width
                        aria-hidden="true"
                    />
                </Link>
            </dl>
        </div>

        <!-- Section: Manufacture -->
        <div>
            <div class="flex items-center gap-x-2 mb-4 pb-2 border-b border-gray-200">
                <FontAwesomeIcon icon="fal fa-shredder" class="text-indigo-500" fixed-width aria-hidden="true" />
                <h2 class="text-base font-semibold text-gray-700">Manufacture</h2>
            </div>
            <dl class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                <Link
                    v-for="(stat, idx) in manufactureStats"
                    :key="idx"
                    :href="route(stat.route.name)"
                    class="relative overflow-hidden rounded-lg bg-white border border-gray-200 px-4 py-5 hover:bg-gray-50 transition-colors"
                >
                    <dt class="truncate text-sm font-medium text-gray-400">{{ stat.label }}</dt>
                    <dd class="mt-2 flex items-baseline gap-x-2">
                        <span class="text-3xl font-semibold tracking-tight" :style="{ color: stat.color }">
                            <CountUp :endVal="stat.value" :duration="1.2" :scrollSpyOnce="true" />
                        </span>
                    </dd>
                    <FontAwesomeIcon
                        :icon="stat.icon"
                        class="absolute top-4 right-4 text-2xl opacity-20"
                        :style="{ color: stat.color }"
                        fixed-width
                        aria-hidden="true"
                    />
                </Link>
            </dl>
        </div>

        <!-- Section: Layout -->
        <div>
            <div class="flex items-center gap-x-2 mb-4 pb-2 border-b border-gray-200">
                <FontAwesomeIcon icon="fal fa-layer-group" class="text-emerald-500" fixed-width aria-hidden="true" />
                <h2 class="text-base font-semibold text-gray-700">Layout</h2>
            </div>
            <dl class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                <Link
                    v-for="(stat, idx) in layoutStats"
                    :key="idx"
                    :href="route(stat.route.name)"
                    class="relative overflow-hidden rounded-lg bg-white border border-gray-200 px-4 py-5 hover:bg-gray-50 transition-colors"
                >
                    <dt class="truncate text-sm font-medium text-gray-400">{{ stat.label }}</dt>
                    <dd class="mt-2 flex items-baseline gap-x-2">
                        <span class="text-3xl font-semibold tracking-tight" :style="{ color: stat.color }">
                            <CountUp :endVal="stat.value" :duration="1.2" :scrollSpyOnce="true" />
                        </span>
                    </dd>
                    <FontAwesomeIcon
                        :icon="stat.icon"
                        class="absolute top-4 right-4 text-2xl opacity-20"
                        :style="{ color: stat.color }"
                        fixed-width
                        aria-hidden="true"
                    />
                </Link>
            </dl>
        </div>

    </div>
</template>
